import { useEffect, useState } from "react";
import { MapPicker } from "components/MapPicker";
import { Button, WithActions } from "components";
import { useGetMunicipalitiesQuery } from "services";
import { transformMunicipalitiesToOptions } from "helpers";
import { t } from "i18next";

interface FormData {
  parking_name_mk: string;
  parking_name_al: string;
  parking_name_en: string;
  longitude: string;
  latitude: string;
  municipality_id: number;
  area_type: string;
  total_parking_slots: number;
  payment_options: number[];
  amenities: number[];
}

interface MunicipalityGeoStepProps {
  form: FormData;
  handleChange: (key: keyof FormData, value: string | number | number[]) => void;
  errorMessage: string | null;
  onClose: () => void;
  prevStep: () => void;
  nextStep: () => void;
  isLoading: boolean;
  getParkingNameByLanguage: () => string;
}

export const MunicipalityGeoStep: React.FC<MunicipalityGeoStepProps> = ({
  form,
  handleChange,
  errorMessage,
  onClose,
  prevStep,
  nextStep,
  isLoading,
  getParkingNameByLanguage,
}) => {
  const {
    data: municipalities = [],
    isLoading: isMunicipalitiesLoading,
    isError: isMunicipalitiesError,
  } = useGetMunicipalitiesQuery();

  const [mapCenter, setMapCenter] = useState<[number, number]>([42.0038, 21.4522]);
  const [useCoordinates, setUseCoordinates] = useState(false);
  const [useMap, setUseMap] = useState(false);

  const municipalityOptions = transformMunicipalitiesToOptions(municipalities);

  useEffect(() => {
    if (form.municipality_id && municipalities.length > 0) {
      const selectedMunicipality = municipalities.find((m) => m.id === form.municipality_id);
      if (selectedMunicipality) {
        if (!useCoordinates) {
          handleChange("latitude", "");
          handleChange("longitude", "");
        }

        const lat = parseFloat(selectedMunicipality.latitude);
        const lng = parseFloat(selectedMunicipality.longitude);
        setMapCenter([lat, lng]);
      }
    } else {
      setUseCoordinates(false);
      setUseMap(false);
    }
  }, [form.municipality_id, municipalities]);

  if (isMunicipalitiesLoading) return <div>Loading municipalities...</div>;
  if (isMunicipalitiesError) return <div>Error loading municipalities</div>;

  return (
    <div>
      <h3 className="text-xs font-light uppercase text-primary">
        Configure Parking ({getParkingNameByLanguage() || "PARKING NAME"})
      </h3>
      <div className="mt-2 space-y-2">
        <h4 className="font-semibold">Municipality*</h4>
        <select
          value={form.municipality_id || 0}
          onChange={(e) => handleChange("municipality_id", Number(e.target.value))}
          className="w-full border px-3 py-2"
        >
          <option value={0}>Select Municipality</option>
          {municipalityOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <h4 className="font-semibold">Geolocation*</h4>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={useCoordinates}
            onChange={(e) => {
              const checked = e.target.checked;
              setUseCoordinates(checked);
              if (checked && form.municipality_id) {
                const selectedMunicipality = municipalities.find((m) => m.id === form.municipality_id);
                if (selectedMunicipality) {
                  handleChange("latitude", selectedMunicipality.latitude);
                  handleChange("longitude", selectedMunicipality.longitude);
                }
              } else {
                handleChange("latitude", "");
                handleChange("longitude", "");
              }
            }}
            disabled={!form.municipality_id}
          />
          Use Coordinates
        </label>

        <div className="flex gap-2">
          <input
            value={form.longitude}
            onChange={(e) => handleChange("longitude", e.target.value)}
            className="w-1/2 border px-3 py-2"
            placeholder="Longitude"
            readOnly={!useCoordinates}
          />
          <input
            value={form.latitude}
            onChange={(e) => handleChange("latitude", e.target.value)}
            className="w-1/2 border px-3 py-2"
            placeholder="Latitude"
            readOnly={!useCoordinates}
          />
        </div>

        <div className="flex gap-2 text-xs text-gray-500">
          <a href="https://www.latlong.net/" target="_blank" className="underline">
            {t("parkingManagement.getCoordinates")}
          </a>
          <a href="https://maps.google.com" target="_blank" className="underline">
            {t("parkingManagement.goToMaps")}
          </a>
        </div>

        <label className="flex items-center gap-2 pt-4">
          <input
            type="checkbox"
            checked={useMap}
            onChange={(e) => setUseMap(e.target.checked)}
            disabled={!form.municipality_id}
          />
          {t("parkingManagement.useMap")}
        </label>

        {useMap && (
          <div className="space-y-2">
            <MapPicker
              onSelect={(lat, lng) => {
                handleChange("latitude", lat);
                handleChange("longitude", lng);
              }}
              defaultCenter={mapCenter}
              center={form.municipality_id ? mapCenter : undefined}
            />
            <p className="text-xs text-gray-500">{t("parkingManagement.clickToUseMap")}</p>
          </div>
        )}
      </div>

      {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}

      <WithActions
        closeButton={t("common.cancel")}
        setOpen={onClose}
        additionalButtons={
          <>
            <Button onClick={prevStep}>Back</Button>
            <Button onClick={nextStep} disabled={isLoading || !form.municipality_id}>
              {t("common.continue")}
            </Button>
          </>
        }
      />
    </div>
  );
};
